import React from 'react';

export const getScope = () => (
  <div className="title">
    <b>Scope</b>
    <p>
      The dataset has been developed for the examination of prostate core needle biopsies. The
      elements and associated commentary apply to invasive carcinomas of the prostate gland.
      Urothelial carcinomas arising in the bladder or urethra are dealt with in a separate dataset,
      while urothelial carcinomas arising in the prostate are included in this dataset.
    </p>
  </div>
);
export const getNote1_clinicalInformation = () => (
  <div className="note note_1">
    <div className="title">
      <b>Clinical information</b>
      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>
    <div className="paragraphs">
      <p className="paragraph">
        It is the responsibility of the clinician requesting the pathological examination of a
        specimen to provide information that will have an impact on the diagnostic process or affect
        its interpretation. The use of a standard pathology requisition/request form including a
        checklist of important clinical information is strongly encouraged to help ensure that
        relevant clinical data is provided by the clinicians with the specimen. Information about
        prior biopsies or treatment aids interpretation of the microscopic findings and accurate
        pathological diagnosis, while knowledge of the number of needle cores taken from each site
        aids pathological assessment of the number of involved cores. Radiation and/or endocrine
        therapy for prostate cancer have a profound effect on the morphology of both the cancer and
        the benign prostatic tissue. For this reason, information about any previous therapy is
        important for the accurate assessment of needle core biopsies.
      </p>

      <p className="paragraph">
        Following irradiation, benign acinar epithelium shows nuclear enlargement and nucleolar
        prominence,
        <sup>1</sup>
        {' '}
while basal cells may show cytological atypia, nuclear enlargement and nuclear
        smudging.
        <sup>2</sup>
        {' '}
There may also be increased stromal fibrosis, which may resemble tumour-induced
        desmoplasia. These changes may persist for a considerable period, having been reported up to
        72 months after treatment, and are more pronounced in patients who have undergone
        brachytherapy compared to those who have received external beam radiation therapy.
        <sup>2,3</sup>
        {' '}
It is important to document any previous radiotherapy to help the pathologist
        to interpret changes accurately. Radiation may be associated with apparent upgrading of
        prostate cancer in prostatectomy specimens.
        <sup>4</sup>
      </p>

      <p className="paragraph">
        Likewise, neoadjuvant androgen deprivation therapy (ADT) may induce morphological changes in
        both prostate cancer and benign tissue. Androgen blockade induces basal cell hyperplasia and
        cytoplasmic vacuolation in benign prostatic tissue, although this is unlikely to be confused
        with malignancy.
        <sup>5</sup>
        {' '}
More significantly from a diagnostic point of view, neoadjuvant ADT may
        increase the risk of overlooking acinar adenocarcinoma on low power microscopic examination
        due to collapse of glandular lumina, cytoplasmic pallor and shrinking of nuclei.
        <sup>6-8</sup>
        {' '}
The effect of androgen blockage on prostate cancer is variable and an
        apparent upgrading of the cancer has been reported in a number of studies.
        <sup>4,5</sup>
        {' '}
Hence, it has been suggested that in biopsies undertaken following either
        radiotherapy or androgen deprivation therapy, tumours that show significant treatment effect
        should not be graded.
        <sup>9</sup>
      </p>

      <p className="paragraph">
        The Gleason grade and score of prostate cancer in any previously submitted specimen should
        also be provided by the clinician as this allows assessment of any progression of the tumour
        towards a higher grade/more undifferentiated state, which itself may be of prognostic
        significance. If the patient is on active surveillance this information should also be
        included.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>
          {' '}
          Cheng L, Cheville JC and Bostwick DG (1999). Diagnosis of prostate cancer in needle
          biopsies after radiation therapy.&nbsp;
          <i>Am J Surg Pathol</i>
          {' '}
23(10):1173–1183.
        </span>
      </li>
      <li className="reference">
        <span>
          {' '}
          Magi-Galluzzi C, Sanderson HBS and Epstein JI (2003). Atypia in non-neoplastic prostate
          glands after radiotherapy for prostate cancer: duration of atypia and relation to type of
          radiotherapy.&nbsp;
          <i>Am J Surg Pathol</i>
          {' '}
27:206–212.
        </span>
      </li>
      <li className="reference">
        <span>
          Herr HW and Whitmore WF, Jr (1982). Significance of prostatic biopsies after radiation
          therapy for carcinoma of the prostate.&nbsp;
          <i>Prostate</i>
          {' '}
3(4):339–350.
        </span>
      </li>
      <li className="reference">
        <span>
          {' '}
          Grignon DJ and Sakr WA (1995). Histologic effects of radiation therapy and total androgen
          blockage on prostate cancer.&nbsp;
          <i>Cancer</i>
          {' '}
75:1837–1841.
        </span>
      </li>
      <li className="reference">
        <span>
          Vailancourt L, Ttu B, Fradet Y, Dupont A, Gomez J, Cusan L, Suburu ER, Diamond P, Candas B
          and Labrie F (1996). Effect of neoadjuvant endocrine therapy (combined androgen blockade)
          on normal prostate and prostatic carcinoma. A randomized study.&nbsp;
          <i>Am J Surg Pathol</i>
          {' '}
20(1):86- 93.
        </span>
      </li>
      <li className="reference">
        <span>
          {' '}
          Montironi R, Magi-Galluzzi C, Muzzonigro G, Prete E, Polito M and Fabris G (1994). Effects
          of combination endocrine treatment on normal prostate, prostatic intraepithelial
          neoplasia, and prostatic adenocarcinoma.
          <i>J Clin Pathol</i>
          &nbsp; 47(10):906-913.
        </span>
      </li>
      <li className="reference">
        <span>
          {' '}
          Civantos F, Marcial MA, Banks ER, Ho CK, Speights VO, Drew PA, Murphy WM and Soloway MS
          (1995). Pathology of androgen deprivation therapy in prostate carcinoma. A comparative
          study of 173 patients.&nbsp;
          <i>Cancer</i>
          {' '}
75(7):1634-1641.
        </span>
      </li>
      <li className="reference">
        <span>
          Bostwick DG and Meiers I (2007). Diagnosis of prostatic carcinoma after therapy.
          {' '}
          <i>Arch Pathol Lab Med</i>
          {' '}
131(3):360-371.
        </span>
      </li>
      <li className="reference">
        <span>
          Epstein JI and Yang XJ (2002). Benign and malignant prostate following treatment. In:
          {' '}
          <i>Prostate Biopsy Interpretation</i>
, Lippincott Williams and Wilkins, Philadelphia,
          Pennsylvania, 209–225.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote2_preBiosySerumPsa = () => (
  <div className="note note_2">
    <div className="title">
      <b>Pre-biopsy serum PSA</b>
      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">
        The clinician requesting the pathological examination should provide information on the
        pre-biopsy serum prostate-specific antigen (PSA) level. The use of a standard pathology
        requisition/request form including a checklist of important clinical information is strongly
        encouraged to help ensure that important clinical data is provided by the clinicians with
        the specimen. Despite criticisms about the utility of PSA-based prostate cancer screening,
        most prostate cancers are detected in asymptomatic men on the basis of PSA testing. Although
        PSA levels provide some indication of the likelihood of discovering cancer within a biopsy
        of the prostate, a diagnosis of malignancy should be based on histological findings and
        should not be influenced by PSA levels.
      </p>
      <p className="paragraph">
        In addition, serum PSA is a key parameter in some nomograms widely used to pre-operatively
        predict the American Joint Committee on Cancer (AJCC)/Union of International Cancer Control
        (UICC) pathological T category of prostate cancer or the risk of recurrence following
        radical prostatectomy and to guide clinical decision making with respect to disease
        management.
        <sup>1</sup>
      </p>
      <p className="paragraph">
        If the patient is on 5-alpha-reductase inhibitor medications, such as finasteride or
        dutasteride, this should be recorded as it may lower serum PSA levels and affect
        interpretation of serum PSA values for detecting prostate cancer.
        <sup>2-4</sup>
      </p>
    </div>
    <hr />

    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>
          Eifler JB, Feng Z, Lin BM, Partin MT, Humphreys EB, Han M, Epstein JI, Walsh PC, Trock BJ
          and Partin AW (2013). An updated prostate cancer staging nomogram (Partin tables) based on
          cases from 2006 to 2011.&nbsp;
          <i>BJU Int</i>
          {' '}
111(1):22-29.
        </span>
      </li>
      <li className="reference">
        <span>
          Guess HA, Gormley GJ, Stoner E and Oesterling JE (1996). The effect of finasteride on
          prostate specific antigen: review of available data.
          <i>J Urol</i>
          &nbsp; 155(1):3-9.
        </span>
      </li>
      <li className="reference">
        <span>
          Oesterling JE, Roy J, Agha A, Shown T, Krarup T, Johansen T, Lagerkvist M, Gormley G, Bach
          M and Waldstreicher J (1997). Biologic variability of prostate-specific antigen and its
          usefulness as a marker for prostate cancer: effects of finasteride. The Finasteride PSA
          Study Group.&nbsp;
          <i>Urology</i>
          {' '}
50(1):13-18.
        </span>
      </li>
      <li className="reference">
        <span>
          Marberger M, Freedland SJ, Andriole GL, Emberton M, Pettaway C, Montorsi F, Teloken C,
          Rittmaster RS, Somerville MC and Castro R (2012). Usefulness of prostate-specific antigen
          (PSA) rise as a marker of prostate cancer in men treated with dutasteride: lessons from
          the REDUCE study.&nbsp;
          <i>BJU Int</i>
          {' '}
109(8):1162-1169.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote3_clinicalStage = () => (
  <div className="note note_3">
    <div className="title">
      <b>Clinical stage</b>
      &nbsp; (Recommended)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">
        The clinician requesting the pathological examination should provide information on the
        clinical stage. The use of a standard pathology requisition/request form including a
        checklist of important clinical information is strongly encouraged to help ensure that
        important clinical data is provided by the clinicians with the specimen. Along with
        pre-biopsy serum prostate-specific antigen (PSA), clinical stage is a vital parameter in
        some nomograms widely used to pre-operatively predict the pathological T category of
        prostate cancer and to guide clinical decision making with respect to disease management.
        <sup>1</sup>
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>
          Eifler JB, Feng Z, Lin BM, Partin MT, Humphreys EB, Han M, Epstein JI, Walsh PC, Trock BJ
          and Partin AW (2013). An updated prostate cancer staging nomogram (Partin tables) based on
          cases from 2006 to 2011.&nbsp;
          <i>BJU Int</i>
          {' '}
111(1):22-29.
        </span>
      </li>
    </ol>
  </div>
);

export const getNote4_specimensSubmitted = () => (
  <div className="note note_3">
    <div className="title">
      <b>Specimens submitted</b>
      &nbsp; (Required)
    </div>
    <b> Reason/Evidentiary Support </b>

    <div className="paragraphs">
      <p className="paragraph">
        Information on specimens submitted for histopathological examination, including location,
        number of needle cores and length of cores, is regarded as an integral and essential part of
        a pathology report.
        <sup>1</sup>
        {' '}
The length of the cores should be measured in the wet specimen before tissue
        processing and paraffin embedding.
      </p>
      <p className="paragraph">
        Preferably there should be only 1 needle core in each specimen jar. However, if 2 or more
        needle cores are submitted in one container and there is some fragmentation, it may not be
        possible to reliably determine the number of involved cores. In this situation the urologist
        should state on the pathology request/requisition form how many cores were submitted in each
        jar to avoid counting fragments of the one core as separate cores (particularly with cores
        {' '}
        {'<'}
6 mm long) and providing misleading information on tumour extent.
        <sup>2</sup>
        {' '}
Where more than 5 cores are submitted in a specimen jar, e.g. with
        saturation/template biopsies, a range may be submitted for length of the cores rather than
        measuring each one individually.
      </p>
    </div>
    <hr />
    <p className="text-bold">References</p>
    <ol className="references">
      <li className="reference">
        <span>
          ICCR (International Collaboration on Cancer Reporting) (2017). Guidelines for the
          development of ICCR datasets. Available from:
          {' '}
          <i>http://www.iccr-cancer.org/datasets/ dataset-development</i>
          {' '}
(Accessed 1st March 2017).
          {' '}
        </span>
      </li>
      <li className="reference">
        <span>
          Amin MB, Lin DW, Gore JL, Srigley JR, Samaratunga H, Egevad L, Rubin M, Nacey J, Carter
          HB, Klotz L, Sandler H, Zietman AL, Holden S, Montironi R, Humphrey PA, Evans AJ, Epstein
          JI, Delahunt B, McKenney JK, Berney D, Wheeler TM, Chinnaiyan AM, True L, Knudsen B and
          Hammond ME (2014). The critical role of the pathologist in determining eligibility for
          active surveillance as a management option in patients with prostate cancer: consensus
          statement with recommendations supported by the College of American Pathologists,
          International Society of Urological Pathology, Association of Directors of Anatomic and
          Surgical Pathology, the New Zealand Society of Pathologists, and the Prostate Cancer
          Foundation.&nbsp;
          <i>Arch Pathol Lab Med</i>
          {' '}
138(10):1387-1405.
        </span>
      </li>
    </ol>
  </div>
);
